import CloseIcon from '@mui/icons-material/Close';
import { Box, IconButton, Typography, useTheme } from '@mui/material';
import { type FC, useEffect, useState } from 'react';
import { type BannerModel, BannerType, listBanners } from '../../../models';

const SEVERITY_BY_TYPE: Record<BannerType, 'error' | 'warning' | 'info'> = {
  [BannerType.Critical]: 'error',
  [BannerType.Warning]: 'warning',
  [BannerType.Info]: 'info'
};

const DISMISSED_BANNERS_STORAGE_KEY = 'dismissedBannerIds';

const getDismissedIds = (): string[] => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_BANNERS_STORAGE_KEY) ?? '[]');
  } catch {
    return [];
  }
};

export const BannerDisplay: FC = () => {
  const theme = useTheme();
  const [banners, setBanners] = useState<BannerModel[]>([]);
  const [dismissedIds, setDismissedIds] = useState<string[]>(getDismissedIds());

  useEffect(() => {
    (async () => {
      const { result } = await listBanners({ limit: 100, offset: 0 });
      setBanners(result.filter((banner) => banner.isActive));
    })();
  }, []);

  const dismissBanner = (bannerId: string) => {
    const updated = [...dismissedIds, bannerId];
    setDismissedIds(updated);
    sessionStorage.setItem(DISMISSED_BANNERS_STORAGE_KEY, JSON.stringify(updated));
  };

  const visibleBanners = banners.filter((banner) => !dismissedIds.includes(banner.id));

  if (visibleBanners.length === 0) return null;

  return (
    <Box sx={{ mt: 2, mx: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 1 }}>
      {visibleBanners.map((banner) => {
        const severity = SEVERITY_BY_TYPE[banner.type];
        return (
          <Box
            key={banner.id}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              minHeight: 44,
              borderRadius: 1,
              py: 1,
              px: 2,
              backgroundColor: theme.palette[severity].main,
              color: theme.palette[severity].contrastText
            }}
          >
            <Typography variant="body2" sx={{ textAlign: 'flex-start', color: 'inherit' }}>
              {banner.content}
            </Typography>
            {banner.isClearable && (
              <IconButton
                size="small"
                onClick={() => dismissBanner(banner.id)}
                sx={{
                  color: 'inherit',
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
